/**
 * Uživatelé — jen pro superadmina. Seznam uživatelů portálu, do kterého se
 * invoice přihlašuje, a změna jejich hesla. Otevírá se z hlavičky tlačítkem
 * "Uživatelé".
 */
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { fetchUsers, setUserPassword, type PortalUser } from "../api/users.js";
import { useAuth } from "../providers/AuthProvider.js";

interface Props {
  open: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  superadmin: "Superadmin",
  admin: "Admin",
  user: "Uživatel",
  technician: "Technik",
};

/** Čitelné heslo bez zaměnitelných znaků (0/O, 1/l/I). */
function generatePassword(): string {
  const alphabet = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint32Array(14));
  return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join("");
}

export function UsersDialog({ open, onClose }: Props) {
  const { accessToken, user: me } = useAuth();
  const [editing, setEditing] = useState<PortalUser | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["portal-users"],
    queryFn: () => fetchUsers(accessToken ?? ""),
    enabled: open && !!accessToken,
  });

  if (!open) return null;
  const users = query.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">Uživatelé</h2>
          <button
            onClick={() => {
              setEditing(null);
              onClose();
            }}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Zavřít"
          >
            ✕
          </button>
        </div>

        {notice && (
          <div className="mx-4 mt-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
            {notice}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {query.isLoading && (
            <div className="p-4 text-sm text-slate-500">Načítám…</div>
          )}
          {query.isError && (
            <div className="p-4 text-sm text-red-600">
              {query.error instanceof Error
                ? query.error.message
                : String(query.error)}
            </div>
          )}
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left sticky top-0">
              <tr>
                <th className="px-3 py-2">Jméno</th>
                <th className="px-3 py-2">E-mail</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Stav</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className={`border-t border-slate-100 ${u.isActive ? "" : "text-slate-400"}`}
                >
                  <td className="px-3 py-2">
                    {u.name || u.nickname || "—"}
                    {u.id === me?.id && (
                      <span className="ml-2 text-xs text-slate-400">(vy)</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2">
                    {ROLE_LABELS[u.role] ?? u.role}
                  </td>
                  <td className="px-3 py-2">
                    {u.isActive ? "Aktivní" : "Zablokovaný"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => {
                        setNotice(null);
                        setEditing(u);
                      }}
                      className="px-2.5 py-1 text-slate-700 border border-slate-300 rounded hover:bg-slate-100"
                    >
                      Změnit heslo
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <PasswordForm
          target={editing}
          accessToken={accessToken ?? ""}
          onCancel={() => setEditing(null)}
          onDone={(message) => {
            setEditing(null);
            setNotice(message);
          }}
        />
      )}
    </div>
  );
}

function PasswordForm({
  target,
  accessToken,
  onCancel,
  onDone,
}: {
  target: PortalUser;
  accessToken: string;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("Heslo musí mít aspoň 8 znaků.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await setUserPassword(accessToken, target.id, password);
      onDone(
        `Heslo uživatele ${target.email} je změněné.` +
          (result.signedOut ? " Ostatní přihlášení tohoto uživatele skončila." : ""),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Změna hesla selhala");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3"
      >
        <h3 className="text-base font-semibold">Nové heslo</h3>
        <p className="text-sm text-slate-500">
          {target.name || target.email} · {target.email}
        </p>

        <label className="block">
          <span className="text-sm text-slate-600">Heslo</span>
          <div className="mt-1 flex gap-2">
            <input
              type={visible ? "text" : "password"}
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError("");
              }}
              className="flex-1 border border-slate-300 rounded px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => {
                setPassword(generatePassword());
                setVisible(true);
              }}
              className="px-3 text-sm text-slate-700 border border-slate-300 rounded hover:bg-slate-100"
            >
              Vygenerovat
            </button>
          </div>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => setVisible(e.target.checked)}
          />
          Zobrazit heslo
        </label>
        <p className="text-xs text-slate-500">
          Uživateli se ostatní přihlášení ukončí. Heslo mu předejte bezpečným
          kanálem — po uložení už se nedá znovu zobrazit.
        </p>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded hover:bg-slate-100"
          >
            Zrušit
          </button>
          <button
            type="submit"
            disabled={saving || password.length < 8}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-slate-400"
          >
            {saving ? "Ukládám…" : "Uložit heslo"}
          </button>
        </div>
      </form>
    </div>
  );
}
